// <countdown-timer> — per-visitor countdown (e.g. 7 days from the first visit).
// The deadline is stored in a cookie that expires together with the offer, so reloads keep
// counting down and a visitor returning after expiry starts a fresh window.
// An optional hard end date (data-end, ISO 8601) caps the deadline for every visitor.
// At zero it adds .is-expired and dispatches a bubbling `countdown:expired` event.
window.fuegoCountdown = window.fuegoCountdown || {
  getCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  },

  setCookie(name, value, expiresAt) {
    const maxAge = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
    document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAge}; path=/; SameSite=Lax`;
  },

  cookieName(prefix, key) {
    return `${prefix}_${String(key || 'default').replace(/[^a-z0-9_-]/gi, '')}`;
  },

  // Shared by every timer/banner with the same campaign key.
  getDeadline(key, durationMs) {
    const name = this.cookieName('fuego_cd', key);
    let deadline = parseInt(this.getCookie(name), 10);

    if (!deadline) {
      deadline = Date.now() + durationMs;
      try {
        this.setCookie(name, deadline, deadline);
      } catch (e) {
        // Cookies blocked — count down from this page view.
      }
    }

    return deadline;
  },
};

if (!customElements.get('countdown-timer')) {
  customElements.define(
    'countdown-timer',
    class CountdownTimer extends HTMLElement {
      connectedCallback() {
        if (this.interval) return;

        this.units = {
          d: this.querySelector('[data-unit="d"]'),
          h: this.querySelector('[data-unit="h"]'),
          m: this.querySelector('[data-unit="m"]'),
          s: this.querySelector('[data-unit="s"]'),
        };
        this.deadline = this.getDeadline();
        if (!this.deadline) return;

        this.hidden = false;
        this.tick();
        this.classList.add('is-ready');
        this.interval = setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() {
        // The banner moves its sticky bar into the header — don't stop a timer that is only relocating.
        if (this.isConnected) return;
        clearInterval(this.interval);
        this.interval = null;
      }

      getDeadline() {
        const days = parseFloat(this.dataset.days);
        const hours = parseFloat(this.dataset.hours);
        const durationMs = days ? days * 86400000 : (hours || 7 * 24) * 3600000;
        const deadline = window.fuegoCountdown.getDeadline(this.dataset.key, durationMs);

        const end = this.dataset.end ? Date.parse(this.dataset.end) : NaN;
        return isNaN(end) ? deadline : Math.min(deadline, end);
      }

      tick() {
        const remaining = Math.max(0, Math.floor((this.deadline - Date.now()) / 1000));

        this.update('d', Math.floor(remaining / 86400));
        this.update('h', Math.floor((remaining % 86400) / 3600));
        this.update('m', Math.floor((remaining % 3600) / 60));
        this.update('s', remaining % 60);

        if (remaining === 0) {
          clearInterval(this.interval);
          this.classList.add('is-expired');
          this.dispatchEvent(new CustomEvent('countdown:expired', { bubbles: true }));
        }
      }

      update(unit, value) {
        const el = this.units[unit];
        const text = String(value).padStart(2, '0');
        if (!el || el.textContent === text) return;

        el.textContent = text;
        // Restart the CSS tick animation.
        el.classList.remove('is-ticking');
        void el.offsetWidth;
        el.classList.add('is-ticking');
      }
    }
  );
}
